/**
 * Блок «Обсуждение» у карточки записи (УВ-01): лента сообщений и отправка текста.
 * УВ-02: у сообщения — «Создать…» поручение, вопрос к проекту или замечание надзора.
 * Без @упоминаний и файлов в первом срезе.
 */
import {
  can,
  canAnywhere,
  DISCUSSION_SPAWN_KINDS,
  type DiscussionCreatedLink,
  type DiscussionEntityType,
  type DiscussionMessage,
  type DiscussionSpawnKind,
  type DiscussionSpawnResult,
  type Person,
  REMARK_KINDS,
  REMARK_SOURCES,
  type RemarkKind,
  type RemarkSource,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { projectQuery } from '../lib/projects';
import { useMe } from '../lib/session';
import { projectTarget } from '../pages/psd/shared';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { ErrorBox, Spinner, toast } from '../ui/feedback';
import { Field, Input, Select, Textarea } from '../ui/form';
import { Panel } from '../ui/layout';

const keyOf = (entityType: DiscussionEntityType, entityId: string) =>
  ['discussions', entityType, entityId] as const;

const peopleQuery = {
  queryKey: ['people'] as const,
  queryFn: () => api<Person[]>('/people'),
};

function CreatedChips({ items }: { items: DiscussionCreatedLink[] }) {
  if (!items.length) return null;
  return (
    <ul className="mt-2 flex flex-col gap-1">
      {items.map((c) => (
        <li key={`${c.kind}:${c.id}`}>
          <Link
            to={c.path}
            className="inline-flex min-h-11 items-center text-[13px] font-medium text-ink underline-offset-4 hover:underline md:min-h-0"
          >
            {DISCUSSION_SPAWN_KINDS[c.kind]}: {c.title}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function defaultDueDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

function SpawnDialog({
  message,
  projectId,
  onClose,
  onDone,
}: {
  message: DiscussionMessage;
  projectId: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const me = useMe();
  const people = useQuery(peopleQuery);
  const project = useQuery({ ...projectQuery(projectId ?? ''), enabled: Boolean(projectId) });
  const canTask = Boolean(me.tenant?.staff) && canAnywhere(me.access, 'project.view');
  const target = project.data ? projectTarget(project.data) : null;
  const canQuestion =
    Boolean(target) &&
    (can(me.access, 'supervision.respond', target!) || can(me.access, 'supervision.issue', target!));
  const canRemark = Boolean(target) && can(me.access, 'supervision.issue', target!);
  const firstKind: DiscussionSpawnKind = canTask
    ? 'task'
    : canRemark
      ? 'psd_remark'
      : canQuestion
        ? 'psd_question'
        : 'task';
  const [kind, setKind] = useState<DiscussionSpawnKind>(firstKind);
  const [title, setTitle] = useState(message.body.slice(0, 300));
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState(defaultDueDate);
  const [subject, setSubject] = useState(message.body.slice(0, 300));
  const [body, setBody] = useState(message.body);
  const [remarkSource, setRemarkSource] = useState<RemarkSource>('builders');
  const [remarkKind, setRemarkKind] = useState<RemarkKind>('remark');
  const [stopWork, setStopWork] = useState(false);
  const spawn = useMutation({
    mutationFn: () =>
      api<DiscussionSpawnResult>(`/discussions/messages/${message.id}/spawn`, {
        body:
          kind === 'task'
            ? {
                kind: 'task',
                title: title.trim(),
                assigneeId,
                dueDate: dueDate || null,
              }
            : kind === 'psd_question'
              ? {
                  kind: 'psd_question',
                  subject: subject.trim(),
                  body: body.trim(),
                }
              : {
                  kind: 'psd_remark',
                  title: title.trim(),
                  description: body.trim() || null,
                  source: remarkSource,
                  remarkKind,
                  dueDate,
                  assigneeId: assigneeId || null,
                  stopWork,
                },
      }),
    onSuccess: (r) => {
      toast(`Создано: ${DISCUSSION_SPAWN_KINDS[r.created.kind]}`);
      onDone();
      onClose();
    },
  });
  const canSubmit =
    kind === 'task'
      ? title.trim().length >= 3 && Boolean(assigneeId) && canTask
      : kind === 'psd_question'
        ? subject.trim().length >= 3 && body.trim().length >= 3 && canQuestion
        : title.trim().length >= 3 && Boolean(dueDate) && canRemark;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Создать из сообщения"
      description="Поручение, вопрос к проекту или замечание. Связь останется у сообщения и у новой записи."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={spawn.isPending}
            disabled={!canSubmit}
            onClick={() => spawn.mutate()}
          >
            Создать
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field label="Что создать">
          <Select value={kind} onChange={(e) => setKind(e.target.value as DiscussionSpawnKind)}>
            {canTask ? <option value="task">{DISCUSSION_SPAWN_KINDS.task}</option> : null}
            {canQuestion ? <option value="psd_question">{DISCUSSION_SPAWN_KINDS.psd_question}</option> : null}
            {canRemark ? <option value="psd_remark">{DISCUSSION_SPAWN_KINDS.psd_remark}</option> : null}
            {!canTask && !canQuestion && !canRemark ? (
              <option value="task">Нет прав на создание</option>
            ) : null}
          </Select>
        </Field>
        {kind === 'task' ? (
          <>
            <Field label="Что сделать">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
            </Field>
            <Field label="Исполнитель">
              <Select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                <option value="">— выберите —</option>
                {(people.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fullName}
                    {p.position ? `, ${p.position}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Срок">
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          </>
        ) : null}
        {kind === 'psd_question' ? (
          <>
            <Field label="Тема">
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={300} />
            </Field>
            <Field label="Вопрос">
              <Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={10000} />
            </Field>
          </>
        ) : null}
        {kind === 'psd_remark' ? (
          <>
            <Field label="Источник">
              <Select value={remarkSource} onChange={(e) => setRemarkSource(e.target.value as RemarkSource)}>
                {(Object.keys(REMARK_SOURCES) as RemarkSource[]).map((s) => (
                  <option key={s} value={s}>
                    {REMARK_SOURCES[s]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Вид">
              <Select value={remarkKind} onChange={(e) => setRemarkKind(e.target.value as RemarkKind)}>
                {(Object.keys(REMARK_KINDS) as RemarkKind[]).map((k) => (
                  <option key={k} value={k}>
                    {REMARK_KINDS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Суть">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
            </Field>
            <Field label="Описание">
              <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000} />
            </Field>
            <Field label="Ответственный">
              <Select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                <option value="">— без назначения —</option>
                {(people.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.fullName}
                    {p.position ? `, ${p.position}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Срок устранения">
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
            <label className="flex min-h-11 items-center gap-2 text-[14px] text-ink">
              <input
                type="checkbox"
                className="size-5"
                checked={stopWork}
                onChange={(e) => setStopWork(e.target.checked)}
              />
              Остановка работ
            </label>
          </>
        ) : null}
        <ErrorBox error={spawn.error} />
      </div>
    </Dialog>
  );
}

export function DiscussionPanel({
  entityType,
  entityId,
  projectId = null,
}: {
  entityType: DiscussionEntityType;
  entityId: string;
  /** Проект карточки — чтобы понять, можно ли создать вопрос (УВ-02) */
  projectId?: string | null;
}) {
  const qc = useQueryClient();
  const me = useMe();
  const [body, setBody] = useState('');
  const [spawnFor, setSpawnFor] = useState<DiscussionMessage | null>(null);
  const q = useQuery({
    queryKey: keyOf(entityType, entityId),
    queryFn: () => api<DiscussionMessage[]>('/discussions', { query: { entityType, entityId } }),
  });
  const send = useMutation({
    mutationFn: () =>
      api<DiscussionMessage>('/discussions', {
        body: { entityType, entityId, body: body.trim() },
      }),
    onSuccess: () => {
      setBody('');
      qc.invalidateQueries({ queryKey: keyOf(entityType, entityId) });
      qc.invalidateQueries({ queryKey: ['discussions', 'mine'] });
      toast('Сообщение отправлено');
    },
  });
  const canSpawn =
    Boolean(me.tenant?.staff) &&
    (canAnywhere(me.access, 'project.view') ||
      canAnywhere(me.access, 'supervision.respond') ||
      canAnywhere(me.access, 'supervision.issue'));
  return (
    <Panel title="Обсуждение" meta={q.data ? `${q.data.length}` : undefined}>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data!.length ? (
        <ul className="mb-4 flex flex-col gap-3">
          {q.data!.map((m) => (
            <li key={m.id} className="rounded-md bg-surface-2 px-3 py-2">
              <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px] text-ink-2">
                <span className="font-medium text-ink">{m.mine ? 'Вы' : m.authorName}</span>
                <span className="num">{dateTime(m.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap text-[14px] text-ink [overflow-wrap:anywhere]">{m.body}</p>
              <CreatedChips items={m.created} />
              {canSpawn ? (
                <div className="mt-2">
                  <Button size="sm" variant="ghost" onClick={() => setSpawnFor(m)}>
                    Создать…
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 text-[14px] text-ink-2">Пока сообщений нет — напишите первое.</p>
      )}
      <div className="flex flex-col gap-2">
        <Textarea
          rows={3}
          value={body}
          placeholder="Сообщение коллегам по этой записи"
          onChange={(e) => setBody(e.target.value)}
          maxLength={4000}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            loading={send.isPending}
            disabled={body.trim().length < 1}
            onClick={() => send.mutate()}
          >
            Отправить
          </Button>
          <ErrorBox error={send.error} />
        </div>
      </div>
      {spawnFor ? (
        <SpawnDialog
          message={spawnFor}
          projectId={projectId}
          onClose={() => setSpawnFor(null)}
          onDone={() => {
            qc.invalidateQueries({ queryKey: keyOf(entityType, entityId) });
            qc.invalidateQueries({ queryKey: ['tasks'] });
            qc.invalidateQueries({ queryKey: ['questions'] });
            qc.invalidateQueries({ queryKey: ['inbox'] });
          }}
        />
      ) : null}
    </Panel>
  );
}
